import { RSAA, ApiError } from 'redux-api-middleware';
import { PromiseState } from 'react-refetch';
import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';
import { ITopicFilterState } from 'components/TopicFilters/shared';
import { RootState } from 'redux/rootReducer';

const GET_REQUEST = 'OPUS/TOPIC_FILTERS/GET_REQUEST';
const PUT_REQUEST = 'OPUS/TOPIC_FILTERS/PUT_REQUEST';
const SUCCESS = 'OPUS/TOPIC_FILTERS/SUCCESS';
const FAILURE = 'OPUS/TOPIC_FILTERS/FAILURE';
const UPDATE = 'OPUS/TOPIC_FILTERS/UPDATE_STORE';

type TopicFilterActionTypes =
  | typeof GET_REQUEST
  | typeof PUT_REQUEST
  | typeof SUCCESS
  | typeof FAILURE
  | typeof UPDATE;

interface ITopicFilterAction {
  type: TopicFilterActionTypes;
  payload?: any;
  error?: any;
}

const noop: any = undefined;

export const initialTopicFilterState: PromiseState<ITopicFilterState> = {
  pending: false,
  fulfilled: false,
  rejected: false,
  reason: null,
  refreshing: false,
  settled: false,
  meta: null,
  then: noop,
  catch: noop,
  value: {
    country: undefined,
    streaming: undefined,
    download: undefined,
    media: undefined,
    region: undefined,
    genre: undefined,
    release: undefined,
    customRegion: undefined,
    platform: undefined,
  },
};

export default function reducer(
  state: PromiseState<ITopicFilterState> = initialTopicFilterState,
  action: ITopicFilterAction
): PromiseState<ITopicFilterState> {
  switch (action.type) {
    case GET_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
      };
    case PUT_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
        value: {
          ...state.value,
          ...action.payload,
        },
      };
    case SUCCESS:
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
        value: {
          ...action.payload,
        },
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: action.error,
        reason: action.payload,
      };
    case UPDATE: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
        value: {
          ...action.payload,
        },
      };
    }

    default:
      return state;
  }
}

export function saveTopicFilters(
  next: ITopicFilterState
): (dispatch: Dispatch) => any {
  return (dispatch: Dispatch): any => {
    const payload = { ...next };

    return dispatch({
      type: UPDATE,
      payload,
    });
  };
}

function getTopicEndpoint(id: string): string {
  return `/kv/user_${id}_topic_filters`;
}

export function saveTopicFiltersRemote(
  next: ITopicFilterState
): (dispatch: Dispatch, getState: TGetRootState) => Promise<void> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<void> => {
    const { id } = getState().authentication.profile;

    // Here we do a normal fetch instead of dispatching an RSAA,
    // this is because saveTopicFilters() already updates the store.
    // This method simply flushes the filter data to the KV store,
    // but after that finished the redux store doesn't need to be touched again.
    // That's why this is implemenented as 'fire & forget'
    await fetch(
      new Request(getTopicEndpoint(id), {
        body: JSON.stringify(next),
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      })
    );
  };
}

export const loadTopicFilters = (): ((
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any>) => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { id } = getState().authentication.profile;
    const { topicFilters } = getState();

    const actionResponse = await dispatch({
      [RSAA]: {
        bailout: topicFilters.pending || topicFilters.fulfilled,
        types: [GET_REQUEST, SUCCESS, FAILURE],
        endpoint: getTopicEndpoint(id),
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });

    if (actionResponse.error) {
      if ((actionResponse.payload as ApiError<any>).status === 404) {
        return await dispatch(initTopicFilters() as any);
      }
    }

    return actionResponse;
  };
};

export const shouldLoadTopicFilters = (): ((
  dispatch: Dispatch,
  getState: TGetRootState
) => boolean) => {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const { topicFilters, authentication } = getState();
    const { isAuthenticated } = authentication;
    if (!isAuthenticated) {
      return false;
    }
    const { pending, fulfilled } = topicFilters;
    return !pending && !fulfilled;
  };
};

function initTopicFilters(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const topicFilters = initialTopicFilterState.value;
    const { topicFilters: topicFiltersState } = getState();

    // dispatch the action
    return await dispatch({
      [RSAA]: {
        bailout: topicFiltersState.pending || topicFiltersState.fulfilled,
        types: [
          {
            type: PUT_REQUEST,
            payload: topicFilters,
          },
          {
            type: SUCCESS,
            payload: topicFilters,
          },
          FAILURE,
        ],
        endpoint: getTopicEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(topicFilters),
      },
    });
  };
}

export const selectTopicFilters = (state: RootState): ITopicFilterState =>
  state.topicFilters.value;
